import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/auth/hooks/useAuth';
import { useCompanyAuthBrand } from '@/auth/company/CompanyAuthBrand';

type ResetStep = 'request' | 'confirm';

/**
 * Router state for the "set a password" variant (ADR-024 §10): an account
 * made on a Company portal has no password, and fias.io's sign-in and sign-up
 * send it here — the same emailed step as a reset, named for what it is.
 * `codeSent`: the code is already on its way, so start at the code step.
 */
export interface SetPasswordState {
  mode: 'set';
  email: string;
  codeSent?: boolean;
}

function setPasswordState(state: unknown): SetPasswordState | null {
  const s = state as Partial<SetPasswordState> | null;
  return s?.mode === 'set' && typeof s.email === 'string' ? (s as SetPasswordState) : null;
}

export function ForgotPasswordPage() {
  const location = useLocation();
  const setMode = setPasswordState(location.state);
  const [step, setStep] = useState<ResetStep>(setMode?.codeSent ? 'confirm' : 'request');
  const [email, setEmail] = useState(setMode?.email ?? '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  // In the Company-branded flow (ADR-024), back to the branded sign-in.
  const signInPath = useCompanyAuthBrand()?.paths.signIn ?? '/auth/login';

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(pwd)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain a number';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return 'Password must contain a special character';
    return null;
  };

  const handleRequestReset = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setStep('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setIsLoading(true);

    try {
      await confirmForgotPassword(email, code, newPassword);
      navigate(signInPath, {
        state: {
          message: setMode
            ? 'Your password is set. Please sign in.'
            : 'Password reset successful! Please sign in.',
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'confirm') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-foreground">
              {setMode ? 'Set your password' : 'Reset your password'}
            </h1>
            <p className="mt-2 text-muted-foreground">
              Enter the code sent to <strong>{email}</strong>
            </p>
          </div>

          <form onSubmit={handleConfirmReset} className="mt-8 space-y-6">
            {error && (
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">{setMode ? 'Code' : 'Reset code'}</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoComplete="one-time-code"
                  disabled={isLoading}
                  maxLength={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <PasswordInput
                  id="new-password"
                  placeholder="Create a new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  At least 8 characters with uppercase, lowercase, number, and special character.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <PasswordInput
                  id="confirm-password"
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading
                ? setMode
                  ? 'Saving...'
                  : 'Resetting...'
                : setMode
                  ? 'Set password'
                  : 'Reset password'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => setStep('request')}
            >
              Send a new code
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-foreground">
            {setMode ? 'Set your password' : 'Forgot password?'}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {setMode
              ? "Enter your email and we'll send you a code"
              : "Enter your email and we'll send you a reset code"}
          </p>
        </div>

        <form onSubmit={handleRequestReset} className="mt-8 space-y-6">
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Sending...' : setMode ? 'Send code' : 'Send reset code'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Remember your password?{' '}
          <Link to={signInPath} className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
